import { ROLES } from './permissions.js';

export const LMS_USERS_FORMAT = 'lms-users' as const;
export const LMS_USERS_SCHEMA_VERSION = 1 as const;
export const LMS_USERS_PASSWORD_MIN_LENGTH = 8;

const ENVELOPE_KEYS = new Set(['format', 'schemaVersion', 'users', 'skipped']);
const USER_KEYS = new Set([
  'email',
  'name',
  'firstName',
  'lastName',
  'phone',
  'password',
  'roles',
  'status',
  'customFields',
]);
const KNOWN_ROLES = new Set<string>(Object.values(ROLES));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type LmsUserStatus = 'active' | 'inactive';

export type LmsCustomFieldValue = string | string[] | boolean | null;

export interface LmsParsedUser {
  email: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  password: string | null;
  roles: string[];
  status: LmsUserStatus;
  customFields?: Record<string, LmsCustomFieldValue>;
}

export interface LmsUsersSkipped {
  reason: string;
  sourceExcerpt?: string;
}

export interface LmsUsersFile {
  format: typeof LMS_USERS_FORMAT;
  schemaVersion: typeof LMS_USERS_SCHEMA_VERSION;
  users: LmsParsedUser[];
  skipped: LmsUsersSkipped[];
}

export interface LmsUsersParseResult {
  users: LmsParsedUser[];
  skipped: LmsUsersSkipped[];
  errors: string[];
}

export class LmsUsersFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LmsUsersFileError';
  }
}

function unknownKeys(value: Record<string, unknown>, allowed: Set<string>): string[] {
  return Object.keys(value).filter((key) => !allowed.has(key));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalTrimmed(value: unknown, path: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') {
    throw new Error(`${path} debe ser texto o null.`);
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseCustomFieldValue(raw: unknown, path: string): LmsCustomFieldValue {
  if (raw === null) return null;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      throw new Error(`${path} tiene un valor no permitido.`);
    }
    return String(raw);
  }
  if (Array.isArray(raw)) {
    if (raw.some((item) => typeof item !== 'string')) {
      throw new Error(`${path} tiene un valor no permitido.`);
    }
    return raw as string[];
  }
  throw new Error(`${path} tiene un valor no permitido.`);
}

function parseCustomFields(
  raw: unknown,
  path: string,
): Record<string, LmsCustomFieldValue> | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (!isPlainObject(raw)) {
    throw new Error(`${path}.customFields debe ser un objeto.`);
  }
  const result: Record<string, LmsCustomFieldValue> = {};
  for (const [key, value] of Object.entries(raw)) {
    const slug = key.trim();
    if (!slug) {
      throw new Error(`${path}.customFields contiene un slug vacío.`);
    }
    result[slug] = parseCustomFieldValue(value, `${path}.customFields.${slug}`);
  }
  return result;
}

function parseSkipped(raw: unknown): LmsUsersSkipped[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    throw new LmsUsersFileError('skipped debe ser un arreglo.');
  }
  return raw.map((item, index) => {
    if (!isPlainObject(item)) {
      return { reason: `Entrada skipped[${index}] no válida.` };
    }
    const reason =
      typeof item.reason === 'string' && item.reason.trim() ? item.reason.trim() : 'Omitida.';
    const sourceExcerpt =
      typeof item.sourceExcerpt === 'string' && item.sourceExcerpt.trim()
        ? item.sourceExcerpt.trim()
        : undefined;
    return { reason, ...(sourceExcerpt ? { sourceExcerpt } : {}) };
  });
}

function parseUser(raw: unknown, path: string): LmsParsedUser {
  if (!isPlainObject(raw)) {
    throw new Error(`${path} debe ser un objeto.`);
  }
  const extra = unknownKeys(raw, USER_KEYS);
  if (extra.length > 0) {
    throw new Error(`${path} contiene campos no permitidos: ${extra.join(', ')}.`);
  }

  if (typeof raw.email !== 'string' || !raw.email.trim()) {
    throw new Error(`${path}.email es obligatorio.`);
  }
  const email = raw.email.trim().toLowerCase();
  if (!EMAIL_RE.test(email)) {
    throw new Error(`${path}.email no es un correo válido.`);
  }

  const firstName = optionalTrimmed(raw.firstName, `${path}.firstName`);
  const lastName = optionalTrimmed(raw.lastName, `${path}.lastName`);
  const explicitName = optionalTrimmed(raw.name, `${path}.name`);
  const name = explicitName || [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!name) {
    throw new Error(`${path}: indica name o firstName/lastName.`);
  }

  let password: string | null = null;
  if (raw.password !== undefined && raw.password !== null) {
    if (typeof raw.password !== 'string') {
      throw new Error(`${path}.password debe ser texto o null.`);
    }
    const trimmed = raw.password.trim();
    if (trimmed) {
      if (trimmed.length < LMS_USERS_PASSWORD_MIN_LENGTH) {
        throw new Error(
          `${path}.password debe tener al menos ${LMS_USERS_PASSWORD_MIN_LENGTH} caracteres.`,
        );
      }
      password = trimmed;
    }
  }

  let roles = ['student'];
  if (raw.roles !== undefined && raw.roles !== null) {
    if (!Array.isArray(raw.roles)) {
      throw new Error(`${path}.roles debe ser un arreglo.`);
    }
    if (raw.roles.length > 0) {
      if (raw.roles.some((role) => typeof role !== 'string' || !role.trim())) {
        throw new Error(`${path}.roles solo admite slugs de texto.`);
      }
      roles = raw.roles.map((role) => (role as string).trim());
      const unknown = roles.filter((role) => !KNOWN_ROLES.has(role));
      if (unknown.length > 0) {
        throw new Error(`${path}.roles contiene valores no permitidos: ${unknown.join(', ')}.`);
      }
    }
  }

  let status: LmsUserStatus = 'active';
  if (raw.status !== undefined && raw.status !== null) {
    if (raw.status !== 'active' && raw.status !== 'inactive') {
      throw new Error(`${path}.status debe ser "active" o "inactive".`);
    }
    status = raw.status;
  }

  const customFields = parseCustomFields(raw.customFields, path);

  return {
    email,
    name,
    firstName,
    lastName,
    phone: optionalTrimmed(raw.phone, `${path}.phone`),
    password,
    roles,
    status,
    ...(customFields !== undefined ? { customFields } : {}),
  };
}

export function parseLmsUsersJson(content: string): LmsUsersParseResult {
  const normalized = content.replace(/^\uFEFF/, '').trim();
  if (!normalized) {
    throw new LmsUsersFileError('El archivo JSON está vacío.');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(normalized);
  } catch {
    throw new LmsUsersFileError('El archivo no es un JSON válido.');
  }

  if (!isPlainObject(parsed)) {
    throw new LmsUsersFileError('El JSON debe ser un objeto lms-users.');
  }

  const extra = unknownKeys(parsed, ENVELOPE_KEYS);
  if (extra.length > 0) {
    throw new LmsUsersFileError(`El archivo contiene campos no permitidos: ${extra.join(', ')}.`);
  }
  if (parsed.format !== LMS_USERS_FORMAT) {
    throw new LmsUsersFileError(`format debe ser "${LMS_USERS_FORMAT}".`);
  }
  if (parsed.schemaVersion !== LMS_USERS_SCHEMA_VERSION) {
    throw new LmsUsersFileError(`schemaVersion debe ser ${LMS_USERS_SCHEMA_VERSION}.`);
  }
  if (!Array.isArray(parsed.users)) {
    throw new LmsUsersFileError('users debe ser un arreglo.');
  }

  const skipped = parseSkipped(parsed.skipped);
  const users: LmsParsedUser[] = [];
  const errors: string[] = [];

  for (const [index, item] of parsed.users.entries()) {
    const path = `users[${index}]`;
    try {
      users.push(parseUser(item, path));
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Usuario inválido.');
    }
  }

  return { users, skipped, errors };
}

export const LMS_USERS_EXAMPLE: LmsUsersFile = {
  format: LMS_USERS_FORMAT,
  schemaVersion: LMS_USERS_SCHEMA_VERSION,
  users: [
    {
      email: 'ana@ejemplo.cl',
      name: 'Ana Pérez',
      firstName: 'Ana',
      lastName: 'Pérez',
      phone: null,
      password: null,
      roles: ['student'],
      status: 'active',
      customFields: {
        'fecha-de-nacimiento': '1990-05-12',
        abono: '150000',
        activo: true,
        intereses: ['musica', 'deporte'],
      },
    },
    {
      email: 'profe@ejemplo.cl',
      name: 'Luis Soto',
      firstName: 'Luis',
      lastName: 'Soto',
      phone: null,
      password: 'Temporal8',
      roles: ['teacher'],
      status: 'active',
    },
  ],
  skipped: [],
};

export interface LmsCustomFieldCatalogItem {
  slug: string;
  name?: string;
  type: string;
  options?: Array<{ value: string; label?: string; isActive?: boolean }>;
}

function isImportableCatalogField(field: LmsCustomFieldCatalogItem): boolean {
  const slug = field.slug.trim();
  const type = field.type.toLowerCase();
  return Boolean(slug) && type !== 'image' && type !== 'file';
}

function placeholderForCustomField(field: LmsCustomFieldCatalogItem): LmsCustomFieldValue {
  const type = field.type.toLowerCase();
  if (type === 'checkbox' || type === 'switch') return false;
  if (type === 'multiselect') return [];
  if (type === 'select' || type === 'radio') {
    const option = (field.options ?? []).find((item) => item.isActive !== false && item.value.trim());
    return option?.value ?? null;
  }
  return null;
}

export function customFieldsTemplateFromCatalog(
  fields: LmsCustomFieldCatalogItem[],
): Record<string, LmsCustomFieldValue> {
  const result: Record<string, LmsCustomFieldValue> = {};
  for (const field of fields) {
    if (!isImportableCatalogField(field)) continue;
    result[field.slug.trim()] = placeholderForCustomField(field);
  }
  return result;
}

export function lmsUsersExampleWithCustomFields(
  customFields: Record<string, LmsCustomFieldValue>,
): LmsUsersFile {
  const [first, ...rest] = LMS_USERS_EXAMPLE.users;
  const firstUser: LmsParsedUser = { ...first };
  delete firstUser.customFields;
  if (Object.keys(customFields).length > 0) {
    firstUser.customFields = customFields;
  }
  return {
    ...LMS_USERS_EXAMPLE,
    users: [firstUser, ...rest],
  };
}

const CHOICE_FIELD_TYPES = new Set(['select', 'radio', 'multiselect']);

function formatOptionForAi(option: { value: string; label?: string; isActive?: boolean }): string | null {
  if (option.isActive === false) return null;
  const value = option.value.trim();
  const label = option.label?.trim() ?? '';
  if (!value && !label) return null;
  if (label && value && label !== value) return `"${label}" (${value})`;
  return `"${label || value}"`;
}

function describeCatalogFieldForAi(field: LmsCustomFieldCatalogItem): string {
  const slug = field.slug.trim();
  const type = field.type.toLowerCase();
  const name = field.name?.trim() || slug;
  const head = `- ${name} (slug: ${slug}, tipo: ${type})`;
  if (!CHOICE_FIELD_TYPES.has(type)) return head;
  const options = (field.options ?? [])
    .map(formatOptionForAi)
    .filter((item): item is string => Boolean(item));
  if (options.length === 0) return head;
  return `${head}. Opciones: ${options.join(', ')}`;
}

export function lmsUsersAiPromptFromCatalog(fields: LmsCustomFieldCatalogItem[]): string {
  const importable = fields.filter(isImportableCatalogField);
  const line =
    importable.length > 0
      ? [
          'Campos personalizados actuales (usa slug o nombre; en select/radio/multiselect también la etiqueta de la opción; no inventes; no incluyas image ni file):',
          ...importable.map(describeCatalogFieldForAi),
        ].join('\n')
      : 'Ahora no hay campos personalizados importables. Omite customFields.';
  return `${LMS_USERS_AI_PROMPT}\n${line}`;
}

export const LMS_USERS_AI_PROMPT = `Eres un conversor de listados de usuarios a JSON LMS. Devuelve ÚNICAMENTE un JSON válido, sin markdown ni comentarios, con este sobre:

{
  "format": "lms-users",
  "schemaVersion": 1,
  "users": [],
  "skipped": []
}

Cada usuario solo puede tener: email, name, firstName, lastName, phone, password, roles, status, customFields.
Reglas:
- email obligatorio, válido.
- name o firstName/lastName (si falta name, concatenar firstName y lastName).
- password opcional (mínimo 8 caracteres). Si no hay, omítelo; el LMS generará una temporal.
- roles: arreglo de slugs developer | super-admin | admin | teacher | student. Si no hay, el LMS usará student.
- status: "active" o "inactive". Si no hay, active.
- phone opcional.
- customFields opcional: objeto { slug o nombre del campo: valor }. Valores: texto, número, boolean, null o arreglo de textos. En select/radio/multiselect puedes usar la etiqueta visible o el valor interno de la opción. Usa campos reales del LMS; no inventes. No incluyas campos de imagen ni archivo.
No inventes id ni campos extra.
Si una fila de origen no se puede mapear, no la conviertas: agrégala a skipped como { "reason": "...", "sourceExcerpt": "..." }.`;
